import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useI18n } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { UploadCloud, ChevronDown, Sparkles, Wand2, CheckCircle2, CircleDashed, Plus, Trash2 } from "lucide-react";
import { ASSET_FIELDS, LIAB_FIELDS, INCOME_FIELDS, emptyPeriod, demoPeriod } from "@/lib/fields";

const ALL_FIELDS = [...ASSET_FIELDS, ...LIAB_FIELDS, ...INCOME_FIELDS];

function FieldGrid({ fields, values, onChange, lang, testPrefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
      {fields.map((f) => (
        <div key={f.k} className="space-y-1.5">
          <Label htmlFor={`${testPrefix}-${f.k}`} className="text-xs text-slate-300">{lang === "it" ? f.it : f.en}</Label>
          <Input id={`${testPrefix}-${f.k}`} type="number" step="any" inputMode="decimal"
            data-testid={`${testPrefix}-${f.k}`}
            className="font-mono-fin"
            value={values[f.k]} placeholder="0"
            onChange={(e) => onChange(f.k, e.target.value)} />
        </div>
      ))}
    </div>
  );
}

function PeriodSection({ prefix, values, setValues, lang, t }) {
  const onChange = (k, v) => setValues((p) => ({ ...p, [k]: v }));
  return (
    <div className="space-y-8">
      <div>
        <h3 className="font-heading text-base font-semibold text-white mb-4">{t("section_assets")}</h3>
        <FieldGrid fields={ASSET_FIELDS} values={values} onChange={onChange} lang={lang} testPrefix={prefix} />
      </div>
      <div>
        <h3 className="font-heading text-base font-semibold text-white mb-4">{t("section_liab")}</h3>
        <FieldGrid fields={LIAB_FIELDS} values={values} onChange={onChange} lang={lang} testPrefix={prefix} />
      </div>
      <div>
        <h3 className="font-heading text-base font-semibold text-white mb-4">{t("section_income")}</h3>
        <FieldGrid fields={INCOME_FIELDS} values={values} onChange={onChange} lang={lang} testPrefix={prefix} />
      </div>
    </div>
  );
}

export default function NewAnalysis() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [company, setCompany] = useState("");
  const [year, setYear] = useState("2025");
  const [sector, setSector] = useState("");
  const [current, setCurrent] = useState(emptyPeriod());
  const [priorYears, setPriorYears] = useState([]);
  const yidRef = useRef(0);
  const [beh, setBeh] = useState({
    centrale_rischi_trend: "stabile", ritardi_pagamenti: "nessuno",
    utilizzo_fidi: "0", sconfinamenti: false, anni_attivita: "5",
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("manual");
  const [sectors, setSectors] = useState([]);
  const [uploadSummary, setUploadSummary] = useState(null);

  useEffect(() => {
    api.get("/sectors").then((r) => setSectors(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const labelOf = (k) => {
    const f = ALL_FIELDS.find((x) => x.k === k);
    return f ? (lang === "it" ? f.it : f.en) : k;
  };

  const toNum = (obj) => {
    const o = {};
    Object.keys(obj).forEach((k) => { o[k] = obj[k] === "" || obj[k] === null ? 0 : parseFloat(obj[k]) || 0; });
    return o;
  };

  const addPriorYear = () => {
    if (priorYears.length >= 4) return;
    const baseYear = parseInt(year) || new Date().getFullYear();
    const suggested = String(baseYear - (priorYears.length + 1));
    yidRef.current += 1;
    setPriorYears((p) => [...p, { id: yidRef.current, year: suggested, values: emptyPeriod() }]);
  };
  const removePriorYear = (id) => setPriorYears((p) => p.filter((y) => y.id !== id));
  const setPriorYearValues = (id, updater) =>
    setPriorYears((p) => p.map((y) => (y.id === id ? { ...y, values: typeof updater === "function" ? updater(y.values) : updater } : y)));
  const setPriorYearLabel = (id, v) =>
    setPriorYears((p) => p.map((y) => (y.id === id ? { ...y, year: v } : y)));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!(name.endsWith(".csv") || name.endsWith(".pdf") || name.endsWith(".txt"))) {
      toast.error(lang === "it" ? "Formato non supportato. Usa CSV, TXT o PDF." : "Unsupported format. Use CSV, TXT or PDF.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(lang === "it" ? "File troppo grande (max 10MB)." : "File too large (max 10MB).");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/parse-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const filled = {};
      Object.keys(emptyPeriod()).forEach((k) => { filled[k] = data.fields[k] ? String(data.fields[k]) : ""; });
      setCurrent(filled);
      const recognized = ALL_FIELDS.filter((f) => filled[f.k] && parseFloat(filled[f.k]) !== 0).map((f) => labelOf(f.k));
      const missing = ALL_FIELDS.filter((f) => !filled[f.k] || parseFloat(filled[f.k]) === 0).map((f) => labelOf(f.k));
      setUploadSummary({ recognized, missing, count: data.matched_count || 0 });
      if ((data.matched_count || 0) === 0) {
        toast.warning(lang === "it"
          ? "Nessuna voce riconosciuta automaticamente. Verifica il file o inserisci i dati manualmente."
          : "No line items were recognized automatically. Check the file or enter data manually.");
      } else {
        toast.success(`${data.matched_count} ${t("upload_done")}`);
      }
      setTab("manual");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || (lang === "it" ? "Errore durante il caricamento" : "Upload error"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const loadDemo = () => {
    const d = {}; const dp = demoPeriod();
    Object.keys(dp).forEach((k) => { d[k] = String(dp[k]); });
    setCurrent(d);
    setCompany("Acme Manifatture S.r.l.");
    toast.info(lang === "it" ? "Dati dimostrativi caricati" : "Demo data loaded");
  };

  const submit = async () => {
    if (!company.trim()) { toast.error(t("company")); return; }
    setLoading(true);
    const priors = priorYears.map((y) => ({ year: String(y.year), data: toNum(y.values) }));
    const curY = parseInt(year);
    let priorPeriod = null;
    if (priors.length) {
      const before = priors
        .filter((p) => !Number.isNaN(parseInt(p.year)) && parseInt(p.year) < curY)
        .sort((a, b) => parseInt(b.year) - parseInt(a.year));
      priorPeriod = (before[0] || priors[0]).data;
    }
    const history = priors.length
      ? [{ year: String(year), data: toNum(current) }, ...priors]
      : null;
    const payload = {
      company_name: company, fiscal_year: year, sector: sector || null, language: lang,
      current: toNum(current),
      prior: priorPeriod,
      history,
      behavioral: {
        centrale_rischi_trend: beh.centrale_rischi_trend,
        ritardi_pagamenti: beh.ritardi_pagamenti,
        utilizzo_fidi: parseFloat(beh.utilizzo_fidi) || 0,
        sconfinamenti: beh.sconfinamenti,
        anni_attivita: parseInt(beh.anni_attivita) || 0,
      },
    };
    try {
      const { data } = await api.post("/analyses", payload);
      toast.success(lang === "it" ? "Analisi generata" : "Analysis generated");
      navigate(`/analysis/${data.id}`);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error(formatApiError(err.response?.data?.detail));
        navigate("/pricing");
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App bg-slate-900 min-h-screen">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="font-heading text-3xl font-bold tracking-tight text-white">{t("new_title")}</h1>
          <Button variant="outline" size="sm" onClick={loadDemo} data-testid="load-demo-btn">
            <Wand2 className="h-4 w-4 mr-2" /> Demo
          </Button>
        </div>

        {/* Company meta */}
        <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900 p-6 grid grid-cols-1 md:grid-cols-4 gap-5">
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="company">{t("company")}</Label>
            <Input id="company" value={company} data-testid="company-input"
              onChange={(e) => setCompany(e.target.value)} placeholder="Es. Acme S.r.l." />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="year">{t("fiscal_year")}</Label>
            <Input id="year" value={year} data-testid="year-input" onChange={(e) => setYear(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sector">{t("sector")}</Label>
            <Select value={sector} onValueChange={setSector}>
              <SelectTrigger id="sector" data-testid="sector-select"><SelectValue placeholder={t("sector_placeholder")} /></SelectTrigger>
              <SelectContent>
                {sectors.map((s) => (
                  <SelectItem key={s.key} value={s.key} data-testid={`sector-opt-${s.key}`}>
                    {lang === "it" ? s.label_it : s.label_en} ({s.ateco})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Data ingestion */}
        <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900 p-6">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="ingestion-tabs">
              <TabsTrigger value="manual" data-testid="tab-manual">{t("tab_manual")}</TabsTrigger>
              <TabsTrigger value="upload" data-testid="tab-upload">{t("tab_upload")}</TabsTrigger>
            </TabsList>

            <TabsContent value="upload" className="pt-6">
              <p className="text-sm text-slate-300 mb-4">{t("upload_hint")}</p>
              <input ref={fileRef} type="file" accept=".csv,.pdf,.txt" className="hidden"
                onChange={handleUpload} data-testid="file-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
                data-testid="upload-btn" className="h-24 w-full border-dashed">
                <div className="flex flex-col items-center gap-2">
                  <UploadCloud className="h-6 w-6 text-blue-600" />
                  <span>{uploading ? t("analyzing") : t("upload_btn")}</span>
                </div>
              </Button>
            </TabsContent>

            <TabsContent value="manual" className="pt-6">
              {uploadSummary && (
                <div className="mb-6 rounded-xl border border-blue-200 bg-blue-950/60 p-4" data-testid="upload-summary">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-600" />
                    <p className="text-sm font-semibold text-white">{t("upload_summary_title")}</p>
                  </div>
                  <p className="text-xs text-slate-300 mb-3">{t("upload_review_hint")}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1.5 mb-1.5">
                        <CheckCircle2 className="h-3.5 w-3.5" /> {t("upload_recognized")} ({uploadSummary.recognized.length})
                      </p>
                      <div className="flex flex-wrap gap-1.5" data-testid="upload-recognized">
                        {uploadSummary.recognized.map((l) => (
                          <span key={l} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">{l}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-400 flex items-center gap-1.5 mb-1.5">
                        <CircleDashed className="h-3.5 w-3.5" /> {t("upload_missing")} ({uploadSummary.missing.length})
                      </p>
                      <div className="flex flex-wrap gap-1.5" data-testid="upload-missing">
                        {uploadSummary.missing.map((l) => (
                          <span key={l} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">{l}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="mb-4">
                <span className="text-xs font-semibold tracking-wider uppercase text-blue-600">{t("section_current")}</span>
              </div>
              <PeriodSection prefix="cur" values={current} setValues={setCurrent} lang={lang} t={t} />

              <div className="mt-8 border-t border-slate-800 pt-6">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <span className="text-sm font-medium text-slate-200">{t("section_prior_years")}</span>
                    <p className="text-xs text-slate-500 mt-0.5 max-w-lg">{t("prior_years_hint")}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={addPriorYear} disabled={priorYears.length >= 4}
                    data-testid="add-year-btn">
                    <Plus className="h-4 w-4 mr-1.5" /> {t("add_year")}
                  </Button>
                </div>

                {priorYears.map((y, idx) => (
                  <Collapsible key={y.id} defaultOpen className="mt-5 rounded-xl border border-slate-800 bg-slate-900/50 p-4" data-testid={`prior-year-${idx}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-slate-300">{t("year_label")}</Label>
                        <Input className="h-8 w-24" value={y.year} data-testid={`prior-year-input-${idx}`}
                          onChange={(e) => setPriorYearLabel(y.id, e.target.value)} />
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => removePriorYear(y.id)}
                          data-testid={`remove-year-${idx}`} className="text-red-600 hover:text-red-700">
                          <Trash2 className="h-4 w-4 mr-1.5" /> {t("remove_year")}
                        </Button>
                        <CollapsibleTrigger asChild>
                          <Button variant="outline" size="sm" data-testid={`toggle-year-${idx}`}>
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                    </div>
                    <CollapsibleContent className="pt-5">
                      <PeriodSection prefix={`py${y.id}`} values={y.values}
                        setValues={(u) => setPriorYearValues(y.id, u)} lang={lang} t={t} />
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Behavioral */}
        <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900 p-6">
          <h3 className="font-heading text-base font-semibold text-white mb-5">{t("section_behavioral")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-300">{t("beh_cr")}</Label>
              <Select value={beh.centrale_rischi_trend} onValueChange={(v) => setBeh((p) => ({ ...p, centrale_rischi_trend: v }))}>
                <SelectTrigger data-testid="beh-cr-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="miglioramento">{t("opt_improve")}</SelectItem>
                  <SelectItem value="stabile">{t("opt_stable")}</SelectItem>
                  <SelectItem value="peggioramento">{t("opt_worsen")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-300">{t("beh_pay")}</Label>
              <Select value={beh.ritardi_pagamenti} onValueChange={(v) => setBeh((p) => ({ ...p, ritardi_pagamenti: v }))}>
                <SelectTrigger data-testid="beh-pay-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nessuno">{t("opt_none")}</SelectItem>
                  <SelectItem value="occasionali">{t("opt_occ")}</SelectItem>
                  <SelectItem value="frequenti">{t("opt_freq")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-300">{t("beh_fidi")}</Label>
              <Input type="number" value={beh.utilizzo_fidi} data-testid="beh-fidi-input"
                onChange={(e) => setBeh((p) => ({ ...p, utilizzo_fidi: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-300">{t("beh_anni")}</Label>
              <Input type="number" value={beh.anni_attivita} data-testid="beh-anni-input"
                onChange={(e) => setBeh((p) => ({ ...p, anni_attivita: e.target.value }))} />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={beh.sconfinamenti} onCheckedChange={(v) => setBeh((p) => ({ ...p, sconfinamenti: v }))} data-testid="beh-sconf-switch" />
              <Label className="text-sm text-slate-200">{t("beh_sconf")}</Label>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <Button size="lg" className="h-12 px-8" onClick={submit} disabled={loading} data-testid="analyze-btn">
            <Sparkles className="h-4 w-4 mr-2" />
            {loading ? t("analyzing") : t("analyze_btn")}
          </Button>
        </div>
      </main>
    </div>
  );
}
